import { StoreBadges } from "./StoreBadges";
import GetTheAppDevices from "./GetTheAppDevices";
import { TrackedLink } from "@/components/analytics/TrackedLink";

/**
 * All display copy lives here so copy passes stay a one-block edit.
 * Headline + lede are the approved App Store screenshot-set lines (8/31).
 * Copy law (brand): "hours" never "credits"; member says Reservation,
 * creator says Job; lime is never marketing text.
 */
const COPY = {
  eyebrow: "For Fivestar members & creators",
  headlineAccent: "Earn yours",
  headlineRest: "from the field & the sidelines.",
  lede: "Already with Fivestar? Sign in with the login you already have. Your bookings, jobs and content, wherever you are.",
  personas: [
    {
      chip: "For Organizations",
      text: "Tell us your games. We handle the rest — pro photo and video for every event, booked in a few taps.",
      newcomer: {
        lead: "New to Fivestar?",
        label: "Become a member",
        to: "/become-a-member",
        ctaId: "become_a_member",
      },
    },
    {
      chip: "For Creators",
      text: "Your jobs, pay and uploads in one place — every dollar you earn tracked in your Wallet.",
      newcomer: {
        lead: "Not a Fivestar creator yet?",
        label: "Apply to become a creator",
        to: "/become-a-creator/apply",
        ctaId: "become_a_creator_apply",
      },
    },
  ],
  // The app has no sign-up: only approved members and creators can log in.
  badgesNote: "For current Fivestar members and creators. Sign in with your existing login.",
} as const;

// Site hero's cyan brand-word color — high contrast on the near-black card.
const CYAN = "#6DDAEF";

/**
 * "Get the App" billboard: its own normal-flow section with a TRANSPARENT
 * band (Bill, 8/31) — as it slides over the pinned TrustedByAndIntro, the
 * pinned section's watermark and logos show through around the card. The
 * store-screenshot visual system lives in its native dark — diagonal shards,
 * cyan glow, italic caps — with the app dashboards rebuilt in HTML
 * (GetTheAppDevices) so their UI elements float into the phone frames on
 * scroll.
 */
const GetTheAppSection = () => {
  return (
    <section className="relative bg-transparent pt-14 pb-16 sm:pb-20 md:pt-20 md:pb-28">
      <div className="relative mx-auto max-w-6xl px-4 md:px-6">
      <div className="relative overflow-hidden rounded-[2rem] bg-[#0A0A0A] md:rounded-[2.5rem]">
        {/* Diagonal shard motif — faint rotated planes, echoing the store art */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-64 h-[140%] w-[70%] rotate-[18deg] bg-gradient-to-b from-white/[0.05] to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-56 bottom-[-40%] h-[120%] w-[60%] rotate-[18deg] bg-gradient-to-t from-white/[0.03] to-transparent"
        />
        {/* Cyan glow rising behind the devices */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-30%] right-[-10%] h-[80%] w-[70%] rounded-full opacity-25 blur-3xl"
          style={{
            background: `radial-gradient(closest-side, ${CYAN}, transparent 70%)`,
          }}
        />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Copy column */}
          <div className="px-7 pt-12 pb-2 sm:px-12 sm:pt-16 lg:py-20 lg:pl-16 lg:pr-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: CYAN }}
              />
              {COPY.eyebrow}
            </span>

            <h2 className="mt-6 text-4xl font-black italic uppercase leading-[0.98] text-white sm:text-5xl lg:text-[3.4rem]">
              <span style={{ color: CYAN }}>{COPY.headlineAccent}</span>{" "}
              {COPY.headlineRest}
            </h2>

            <p className="mt-6 max-w-md text-base text-zinc-400 sm:text-lg">
              {COPY.lede}
            </p>

            <ul className="mt-8 space-y-5 border-l border-white/10 pl-5 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 sm:border-l-0 sm:pl-0 lg:block lg:space-y-5 lg:border-l lg:pl-5">
              {COPY.personas.map((p) => (
                <li key={p.chip} className="max-w-md sm:border-l sm:border-white/10 sm:pl-5 lg:border-l-0 lg:pl-0">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                    {p.chip}
                  </span>
                  <p className="mt-1 text-[15px] leading-snug text-zinc-400">
                    {p.text}
                  </p>
                  <p className="mt-1.5 text-[13px] leading-snug text-white">
                    {p.newcomer.lead}{" "}
                    <TrackedLink
                      ctaId={p.newcomer.ctaId}
                      ctaLocation="get_the_app_section"
                      to={p.newcomer.to}
                      className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded"
                      style={{ color: CYAN }}
                    >
                      {p.newcomer.label} →
                    </TrackedLink>
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col items-start gap-3">
              <StoreBadges
                location="get_the_app_section"
                badgeHeightClassName="h-14"
              />
              <span className="max-w-sm text-xs text-zinc-400">{COPY.badgesNote}</span>
            </div>
          </div>

          {/* HTML-built device showcase — elements fly into the frames on scroll */}
          <GetTheAppDevices />
        </div>
      </div>
      </div>
    </section>
  );
};

export default GetTheAppSection;
